import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { MapAreaClickedEvent, MapAreaType } from '../map-events.model';
import { SafeHtml } from '@angular/platform-browser';
import { MapAreaStyle, MapAreasMapping } from '../map-styles.model';
import { ElectionType } from '@shared/clients/api.generated';
import { EnumService } from "@valg-shared/services/enums/enum.service";

interface Rect { x: number; width: number; y: number; height: number }
@Component({
  selector: 'valg-dk-map',
  templateUrl: './map.component.html',
  styleUrls: [ './map.component.scss' ]
})
export class MapComponent implements AfterViewInit, OnInit {
  @Input()
  public electionType!: ElectionType;

  @Input()
  public electionYear!: number;

  @Input()
  public mapUrl!: SafeHtml;

  @Input()
  public width = 384;

  @Input()
  public mapAreaMapping: MapAreasMapping = {};

  @Input()
  public defaultPathStyle: string = MapAreaStyle.FinalResult;

  @Output()
  public areaClicked: EventEmitter<MapAreaClickedEvent> = new EventEmitter<MapAreaClickedEvent>();

  @ViewChild('mapObject') mapElement!: ElementRef<HTMLObjectElement>;

  public height = 489;
  private readonly _ratio = 1.2734375;
  private readonly zoomableAreas = [ 'Odense', 'Aarhus', 'CPH' ]
  private  svg?: SVGSVGElement;
  private zoomOutBtn?: SVGSVGElement;
  private zoomOutText?: SVGSVGElement;
  private buttonRect?: SVGSVGElement;
  private zoomedIn = false;
  private zoomedRegion?: HTMLElement | null;
  private readonly zoomDuration = 1000; // duration of the zoom animation in milliseconds
  private startTimestamp?: number | null;
  private initialViewBox?: DOMRect;
  private targetViewBox?: Rect;
  constructor(private readonly renderer: Renderer2,
              private readonly enumService: EnumService) {
  }

  ngOnInit(): void {
    this.height = this.width * this._ratio;
  }

  ngAfterViewInit(): void {
    this.mapElement.nativeElement.onload = () => {
      this.addMapEventListeners()
      let paths = this.mapElement.nativeElement.contentDocument!.getElementsByTagName("path");

      Array.from(paths).forEach(path => {
        if (path) {
          path.addEventListener('click', this.mapAreaClickedHandler.bind(this));
          this.setGroupStyleAndTitle(path);
        }
      });
    }
  }

  mapAreaClickedHandler(event: Event) {
    const targetElement = event.target as HTMLElement;
    if (targetElement?.id) {
      this.areaClicked.emit(
        {
          elementId: targetElement.id,
          areaType: this.toAreaType(targetElement.dataset['areaType']),
          data: this.mapAreaMapping[targetElement.id]
        });
    }
  }

  private setGroupStyleAndTitle(pathElement: SVGPathElement): void {
    const parent = pathElement.parentElement;
    if (parent?.tagName != 'g') return;

    if (pathElement.id && this.mapAreaMapping[pathElement.id]) {
      const areaData = this.mapAreaMapping[pathElement.id];
      parent.classList.remove(this.defaultPathStyle);
      parent.classList.add(areaData.style);
      const title = pathElement.getElementsByTagName("title")[0];
      title.style.whiteSpace = "pre";
      title.textContent = `${areaData.displayName}\r\n${this.enumService.translateValue(areaData.style, "MapAreaStyle")}`;
    } else {
      parent.classList.add(this.defaultPathStyle);
    }
  }

  private toAreaType(areaType: string | undefined): MapAreaType | undefined {
    switch (areaType) {
      case 'district':
        return MapAreaType.District;
      case 'municipality':
        return MapAreaType.Municipality;
      case 'region':
        return MapAreaType.Region;
      default:
        return undefined;
    }
  }

  private addMapEventListeners() {
    this.addDimmingEffectsOnHovering();
    this.addZoomingEffects(this.zoomableAreas, this.mapElement.nativeElement.contentDocument!);
  }

  private addDimmingEffectsOnHovering() {
    const regions = this.mapElement.nativeElement.contentDocument!.querySelectorAll(`.map_area`);
    regions.forEach((child) => {
      this.renderer.listen(child, 'mouseleave', () => {
        handleMouseLeave()
      });
      this.renderer.listen(child, 'mouseenter', (event: Event) => {
        handleMouseEnter(event)
      });
    });

    function handleMouseEnter(event: Event) {
      regions?.forEach(child => {
        if (event.target === child) return;

        child.classList.add('is-dimmed');
      })
    }

    function handleMouseLeave() {
      regions?.forEach(child => {
        child.classList.remove('is-dimmed');
      })
    }
  }

  public addZoomingEffects(zoomableAreas: string[], contentDocument: Document) {
    this.svg = contentDocument.getElementById('map') as unknown as SVGSVGElement;
    this.zoomOutBtn = contentDocument.getElementById('zoom-out-btn') as unknown as SVGSVGElement;
    this.zoomOutText = contentDocument.getElementById('zoom-out-text') as unknown as SVGSVGElement;
    this.buttonRect = contentDocument.getElementById('button-rect') as unknown as SVGSVGElement;
      if(this.svg == null || this.zoomOutBtn == null || this.zoomOutText == null || this.buttonRect == null){
        return;
      }
      else{
        this.initialViewBox = this.svg.viewBox.baseVal;
        const unZoomedViewBox = {
          x: this.svg.viewBox.baseVal.x,
          y: this.svg.viewBox.baseVal.y,
          width: this.svg.viewBox.baseVal.width,
          height: this.svg.viewBox.baseVal.height
        };

        // Zoom out button
        this.renderer.listen(this.zoomOutBtn, 'click', () => { this.zoomOut(unZoomedViewBox); })

        // Add event listeners to each zoomable areas
        zoomableAreas.forEach(area => {
          const region = contentDocument.getElementById(area);
          if (region) {
            this.renderer.listen(region, 'click', () => { this.onZoomableClick.bind(this)(region); })
          }
        })
      }
  }

  private onZoomableClick(region: HTMLElement) {
    this.zoomedRegion = region;
    this.initialViewBox = this.svg?.viewBox.baseVal;
    const childBBoxes = (Array.from(region.getElementsByTagName("path"))).map(x => x.getBBox())
    region.setAttribute("class", "not_displayed");
    let parentBBox = childBBoxes[0];
    let maxX = parentBBox.x;
    let maxY = parentBBox.y;
    for (const childBBox of childBBoxes) {
      parentBBox.x = Math.min(parentBBox.x, childBBox.x);
      parentBBox.y = Math.min(parentBBox.y, childBBox.y);
      maxX = Math.max(maxX, childBBox.width + childBBox.x);
      maxY = Math.max(maxY, childBBox.y + childBBox.height);
    }
    parentBBox.width = maxX - parentBBox.x;
    parentBBox.height = maxY - parentBBox.y;
    const bbox = parentBBox;

    this.targetViewBox = { x: bbox.x - 2000, y: bbox.y, width: bbox.width + 4000, height: bbox.height };
    this.zoomedRegion = region;
    this.zoomedIn = true;
    // Start the animation
    window.requestAnimationFrame(this.animateZoom.bind(this));
  }

  private zoomOut(unZoomedViewBox: Rect) {
    if (this.zoomedIn) {
      this.initialViewBox = this.svg?.viewBox.baseVal;
      this.targetViewBox = { x: unZoomedViewBox.x, y: unZoomedViewBox.y, width: unZoomedViewBox.width, height: unZoomedViewBox.height };
      window.requestAnimationFrame(this.animateZoom.bind(this));
      this.zoomedIn = false;
      this.updateZoomOutBtnVisibility.bind(this)();
      this.updateZoomOutFontSize.bind(this)();
      this.updateZoomOutPosition.bind(this)();
      this.zoomedRegion?.setAttribute("class", "zoomable");
      this.zoomedRegion = null;
    }
  }

  private animateZoom(timestamp: number) {
    if (!this.startTimestamp) {
      this.startTimestamp = timestamp;
    }
    const progress = Math.min((timestamp - this.startTimestamp) / this.zoomDuration, 1);
    if(this.initialViewBox && this.targetViewBox)
    {
      const currentViewBox = {
        x: this.initialViewBox.x + (this.targetViewBox.x - this.initialViewBox.x) * progress,
        y: this.initialViewBox.y + (this.targetViewBox.y - this.initialViewBox.y) * progress,
        width: this.initialViewBox.width + (this.targetViewBox.width - this.initialViewBox.width) * progress,
        height: this.initialViewBox.height + (this.targetViewBox.height - this.initialViewBox.height) * progress
      };
      this.svg?.setAttribute('viewBox',
        currentViewBox.x + ' ' + currentViewBox.y + ' ' + currentViewBox.width + ' ' + currentViewBox.height);
    }

    if (progress < 0.5) {
      window.requestAnimationFrame(this.animateZoom.bind(this));
    } else {
      this.updateZoomOutFontSize.bind(this)();
      this.updateZoomOutPosition.bind(this)();
      this.updateZoomOutBtnVisibility.bind(this)();
      this.updateZoomOutFontSize.bind(this)();
      this.updateZoomOutPosition.bind(this)();
      this.updateZoomOutBtnVisibility.bind(this)();
      this.startTimestamp = null;
    }
  }

  private updateZoomOutBtnVisibility() {
    if(this.zoomOutBtn)
    {
      this.zoomOutBtn.style.display = this.zoomedIn ? 'inline' : 'none';
    }
  }

  private updateZoomOutPosition() {
    let viewBox = this.svg!.viewBox.baseVal;
    let ratio = this.svg!.height.baseVal.value / this.svg!.width.baseVal.value;
    let x = viewBox.x + viewBox.width * 0.03;
    let y = viewBox.y + (viewBox.height / 2) - (viewBox.width * ratio / 2) + this.buttonRect!.getBBox().height;
    this.zoomOutBtn?.setAttribute('transform', `translate(${x}, ${y})`);
  }

  private updateZoomOutFontSize() {
    let viewBoxWidth = this.svg!.viewBox.baseVal.width;
    let fontSize = viewBoxWidth * 0.05; // 5% of the viewBox width
    this.zoomOutText?.setAttribute('font-size', fontSize.toString());
    this.fitRectangleToText.bind(this)();
  }

  private fitRectangleToText() {
    let bbox = this.zoomOutText!.getBBox();
    let paddingX = bbox.width * 0.1;
    let paddingY = bbox.height * 0.1;

    this.buttonRect?.setAttribute('x', (bbox.x - paddingX / 2).toString());
    this.buttonRect?.setAttribute('y', (bbox.y - paddingY / 2).toString());
    this.buttonRect?.setAttribute('width', (bbox.width + paddingX).toString());
    this.buttonRect?.setAttribute('height', (bbox.height + paddingY).toString());
  }
}
